/**
 * Page /select-parish — sélecteur de paroisse active.
 *
 * Affichée pour :
 *  - les admins globaux (edaci_admin, super_admin) qui n'ont aucune paroisse
 *    primaire et doivent choisir sur laquelle travailler
 *  - les admins globaux qui changent de paroisse (« Voir toutes les
 *    paroisses » du sélecteur) : ils restent sur la liste même quand une
 *    paroisse est déjà active (19/10/2026 : ils étaient renvoyés aussitôt
 *    au tableau de bord de leur paroisse de fidèle)
 *  - un compte sans paroisse de travail ni administration : il ne peut rien
 *    choisir, la page le lui dit (session ouverte avant le refus à la
 *    connexion, rôle d'équipe sans paroisse)
 *
 * Source : `GET /api/micro/parishes/active` qui retourne les paroisses dont
 * `is_active=true` au format slim (id, name, vicariatName, deaneryName).
 */
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Search, Building2, MapPin } from "lucide-react";

import { useAuth } from "@/hooks/use-auth";
import { useParish } from "@/contexts/parish-context";
import { useTranslation } from "@/lib/i18n/i18n";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

type ParishSlim = {
  id: string;
  name: string;
  vicariatName: string | null;
  deaneryName: string | null;
  dioceseName: string | null;
};

export default function SelectParish() {
  const t = useTranslation();
  const { user, logoutMutation } = useAuth();
  const { setCurrentParish, currentParishId, isGlobalAdmin, availableParishes } = useParish();
  const [, navigate] = useLocation();
  const [search, setSearch] = useState("");

  // Si l'utilisateur a déjà une paroisse active, on le renvoie au dashboard
  // (cas où il atterrit ici par erreur). Pas l'administrateur diocésain :
  // il vient ici justement pour changer de paroisse.
  useEffect(() => {
    if (currentParishId && !isGlobalAdmin) {
      navigate("/dashboard");
    }
  }, [currentParishId, isGlobalAdmin, navigate]);

  // Compte connecté sans paroisse de travail ni administration diocésaine :
  // la liste ne servirait à rien (le choix ne serait pas retenu).
  const sansEspace = !!user && !isGlobalAdmin && availableParishes.length === 0;

  const { data, isLoading, error } = useQuery<ParishSlim[]>({
    queryKey: ["/api/micro/parishes/active"],
    enabled: !sansEspace,
  });

  const filtered = useMemo(() => {
    const list = data ?? [];
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        p.vicariatName?.toLowerCase().includes(q) ||
        p.deaneryName?.toLowerCase().includes(q),
    );
  }, [data, search]);

  const handleSelect = (id: string) => {
    setCurrentParish(id);
    navigate("/dashboard");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-primary to-brand-primary-light py-8 sm:py-12 px-4" role="region" aria-label="Selection de paroisse">
      <div className="max-w-3xl mx-auto">
        <div className="text-center text-white mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold mb-2">
            {t("common.selectParish.title")}
          </h1>
          <p className="text-sm sm:text-base text-white/80">
            {user?.fullName} · {t("common.selectParish.subtitle")}
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-4 sm:p-6 dark:bg-gray-800">
          {sansEspace ? (
            <p className="text-center py-10 text-sm text-gray-600 dark:text-gray-300" role="status" data-testid="text-sans-espace-paroisse">
              {t("common.selectParish.noAccess")}
            </p>
          ) : (
          <>
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              type="search"
              placeholder={t("common.selectParish.searchPlaceholder")}
              className="pl-10"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="input-search-parish"
            />
          </div>

          {isLoading && (
            <div className="flex items-center justify-center py-12 text-gray-500 dark:text-gray-300">
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              {t("common.loading")}
            </div>
          )}

          {error && (
            <div className="text-center py-12 text-red-600" data-testid="error-load-parishes">
              {t("common.selectParish.errorLoading")}
            </div>
          )}

          {!isLoading && !error && filtered.length === 0 && (
            <div className="text-center py-12 text-gray-500 dark:text-gray-300" data-testid="empty-parishes">
              {t("common.selectParish.empty")}
            </div>
          )}

          {!isLoading && !error && filtered.length > 0 && (
            <div className="space-y-2 max-h-[60vh] overflow-y-auto">
              {filtered.map((p) => (
                <button
                  key={p.id}
                  onClick={() => handleSelect(p.id)}
                  data-testid={`button-select-parish-${p.id}`}
                  className="w-full text-left p-4 rounded-xl border border-gray-200 hover:border-brand-gold hover:bg-[var(--brand-bg-cream)] transition-all group dark:border-gray-700"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-brand-primary to-brand-primary-light flex items-center justify-center shrink-0">
                      <Building2 className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-brand-primary group-hover:text-brand-gold transition-colors dark:text-gray-100">
                        {p.name}
                      </div>
                      {(p.deaneryName || p.vicariatName) && (
                        <div className="text-xs text-gray-500 mt-1 flex items-center gap-1 dark:text-gray-300">
                          <MapPin className="w-3 h-3 shrink-0" />
                          {[p.deaneryName, p.vicariatName].filter(Boolean).join(" · ")}
                        </div>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
          </>
          )}

          <div className="mt-6 pt-4 border-t border-gray-100 flex justify-end dark:border-gray-700">
            <Button
              variant="ghost"
              onClick={() => logoutMutation.mutate()}
              data-testid="button-logout"
            >
              {t("common.logout")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
